import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Mic, MicOff, Monitor, X, Clock, Shield, MessageSquare, Send, Eye, EyeOff } from 'lucide-react';
import { useCoViewing } from './coviewing/useCoViewing';

export default function CoViewingSession({ session, onEnd }) {
  const {
    connected, peerConnected, muted, elapsedMinutes, chatMessages, chatInput, setChatInput,
    showChat, setShowChat, isPreviewing, togglePreview, toggleMute, sendChat, handleEnd,
  } = useCoViewing(session, onEnd);

  const remainingMin = Math.max(0, 60 - elapsedMinutes);

  return (
    <div className="fixed inset-x-0 bottom-0 z-50" data-testid="co-viewing-bar">
      <div className="bg-slate-900 text-white shadow-lg border-t border-slate-700">
        <div className="max-w-(--breakpoint-xl) mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5"><Monitor className="w-4 h-4 text-emerald-400" /><span className="text-sm font-medium">Co-Viewing Active</span></div>
            <Badge className={`text-xs ${connected ? 'bg-emerald-600' : 'bg-red-600'}`}>{connected ? 'Connected' : 'Disconnected'}</Badge>
            {peerConnected && <Badge className="text-xs bg-blue-600">Support Joined</Badge>}
            {session.masked_mode && <Badge variant="outline" className="text-xs border-amber-500 text-amber-400"><Shield className="w-3 h-3 mr-1" /> Masked Mode</Badge>}
          </div>
          <div className="flex items-center gap-3">
            {session.masked_mode && (
              <Button size="sm" variant="ghost" onClick={togglePreview} className={isPreviewing ? 'text-amber-400 hover:text-amber-300 bg-amber-900/30' : 'text-slate-300 hover:text-white'} data-testid="toggle-mask-preview" title={isPreviewing ? 'Stop previewing masked view' : 'Preview what support sees'}>
                {isPreviewing ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}<span className="text-xs">{isPreviewing ? 'Unpreview' : 'Preview'}</span>
              </Button>
            )}
            <div className="flex items-center gap-1.5 text-sm text-slate-300"><Clock className="w-3.5 h-3.5" /><span>{remainingMin} min remaining</span></div>
            <Button size="sm" variant="ghost" onClick={() => setShowChat(!showChat)} className="text-slate-300 hover:text-white" data-testid="toggle-chat"><MessageSquare className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={toggleMute} className={muted ? 'text-red-400 hover:text-red-300' : 'text-slate-300 hover:text-white'} data-testid="toggle-mute">{muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
            <Button size="sm" variant="destructive" onClick={handleEnd} data-testid="end-session-btn"><X className="w-3.5 h-3.5 mr-1" /> End</Button>
          </div>
        </div>
        {showChat && (
          <div className="border-t border-slate-700 max-w-(--breakpoint-xl) mx-auto px-4 py-2">
            <div className="max-h-32 overflow-y-auto space-y-1 mb-2">
              {chatMessages.length === 0 && <p className="text-xs text-slate-500">No messages yet. Chat with your support specialist.</p>}
              {chatMessages.map((msg, i) => (
                <div key={`msg-${msg.time?.getTime?.() || i}`} className={`text-xs ${msg.from === 'user' ? 'text-emerald-400' : 'text-blue-400'}`}><span className="font-medium">{msg.from === 'user' ? 'You' : 'Support'}:</span> {msg.message}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendChat()} placeholder="Type a message..." className="flex-1 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white placeholder-slate-500" data-testid="chat-input" />
              <Button size="sm" variant="ghost" onClick={sendChat} className="text-emerald-400"><Send className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
